import { useEffect, useState } from 'react';
import type { FC, MouseEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from 'react-query';
import { Box, Card, Container, Divider, Typography } from '@material-ui/core';
import { DateRange } from '@material-ui/lab';
import { JobsTable } from '../../../components/job/jobs-table';
import { Job } from '../../../types/job';
import { useAxios } from '../../../hooks/use-axios';
import { useAuth } from '../../../hooks/use-auth';
import { getDefaultRange, JobsFilter } from '../../../components/job/jobs-filter';
import type { ListResponse, ResponseData } from '../../../types/axios';
import { FilterOperator } from '../../../utils/filter-operators';
import { FilterValue, Sort, EDateFilter, ELastDays } from '../../../types/filter';
import { storage } from '../../../utils/storage';
import { getHelmetTitle } from '../../../utils/utils';
import { EJobsView, getJobsRequestStatusParams } from '../../../utils/jobs-helper';

type JobsData = ListResponse<Job>;

interface Controller {
  filters: Array<FilterValue>;
  page: number;
  range: DateRange<Date>;
  query: string;
  sort: Sort;
  sortBy: string;
  view: EJobsView;
  lastDays?: ELastDays | number;
  dateFilterType?: EDateFilter;
}

const DEFAULT_CONTROLLER: Controller = {
  filters: [],
  page: 0,
  range: getDefaultRange(),
  query: '',
  sort: 'desc',
  sortBy: 'createdAt',
  view: EJobsView.ALL,
  lastDays: undefined,
  dateFilterType: undefined,
};

const getDateRangeFromString = (
  defaultRange: DateRange<Date>,
  from?: string,
  to?: string,
): DateRange<Date> => {
  const fromDate = new Date(from);
  const toDate = new Date(to);

  return Number.isFinite(fromDate.getTime()) && Number.isFinite(toDate.getTime())
    ? ([fromDate, toDate] as DateRange<Date>)
    : defaultRange;
};

const setDefaultControllerFromSearchParams = (searchParams: URLSearchParams): Controller => {
  const storedFilters: (Omit<Controller, 'range'> & { range: [string, string] }) | undefined =
    JSON.parse(storage.controllers.get() || '{}').jobs;

  let { range, filters } = DEFAULT_CONTROLLER;
  // Range in local storage
  if (storedFilters?.range) {
    range = getDateRangeFromString(range, storedFilters?.range[0], storedFilters?.range[1]);
  }
  // Create range from search params 'from' and 'to'
  if (searchParams.get('from') && searchParams.get('to')) {
    range = getDateRangeFromString(range, searchParams.get('from'), searchParams.get('to'));
  }

  // Filters in search params or local storage
  filters =
    (searchParams.get('filters')
      ? JSON.parse(searchParams.get('filters') || '[]')
      : storedFilters?.filters) || filters;

  return {
    filters,
    page: Number(searchParams.get('page')) || storedFilters?.page || DEFAULT_CONTROLLER.page,
    range,
    query: searchParams.get('query') ?? storedFilters?.query ?? DEFAULT_CONTROLLER.query,
    sort: (searchParams.get('sort') as Sort) || storedFilters?.sort || DEFAULT_CONTROLLER.sort,
    sortBy: searchParams.get('sortBy') || storedFilters?.sortBy || DEFAULT_CONTROLLER.sortBy,
    view: (searchParams.get('view') as EJobsView) || storedFilters?.view || DEFAULT_CONTROLLER.view,
    lastDays: storedFilters?.lastDays,
    dateFilterType: storedFilters?.dateFilterType,
  };
};

export const Jobs: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [controller, setController] = useState<Controller>(
    setDefaultControllerFromSearchParams(searchParams),
  );

  const { axios } = useAxios();
  const { tenant } = useAuth();

  const { data, isLoading, error } = useQuery<JobsData>(['jobs', tenant, controller], async () => {
    if (tenant) {
      const [from, to] = controller.range;
      const params: any = {
        start: controller.page * 10,
        length: 10,
        sort: controller.sort.toUpperCase(),
        sortBy: controller.sortBy,
        'globalFilter[tenant]': tenant?.id,
        'globalFilter[createdAt]': {
          operator: FilterOperator.BETWEEN,
          value: [from.toISOString(), new Date(to.setHours(23, 59, 59)).toISOString()],
        },
        ...getJobsRequestStatusParams(controller.view),
      };

      if (controller.query) {
        params['search[item.title]'] = controller.query;
        params['search[item.publicMetadata]'] = {
          operator: FilterOperator.CONTAINS,
          value: { uniqueId: controller.query },
        };
      }

      if (controller.filters.length) {
        controller.filters.forEach((f) => {
          params[`globalFilter[${f.property}]`] = {
            operator: f.operator,
            value: f.value,
          };
        });
      }

      const url = `/jobs`;

      const { data: response } = await axios.get<ResponseData<JobsData>>(url, { params });

      return response?.data;
    }
    throw new Error('Tenant is missing');
  });

  const handlePageChange = (newPage: number): void => {
    setController({
      ...controller,
      page: newPage - 1,
    });
  };

  const handleViewChange = (newView: EJobsView): void => {
    setController({
      ...controller,
      page: 0,
      view: newView,
    });
  };

  const handleRangeChange = (
    dateFilterType: EDateFilter,
    newRange: DateRange<Date>,
    lastDays?: number
  ) => {
    setController({
      ...controller,
      page: 0,
      range: newRange,
      lastDays,
      dateFilterType,
    });
  };

  const handleQueryChange = (newQuery: string): void => {
    setController({
      ...controller,
      page: 0,
      query: newQuery,
    });
  };

  const handleSortChange = (event: MouseEvent<HTMLElement>, property: string): void => {
    const isAsc = controller.sortBy === property && controller.sort === 'asc';

    setController({
      ...controller,
      page: 0,
      sort: isAsc ? 'desc' : 'asc',
      sortBy: property,
    });
  };

  const handleFilterChange = (newFilterValue: FilterValue): void => {
    let updatedFilters = controller.filters.filter((f) => f.property !== newFilterValue.property);
    if (newFilterValue.value) {
      updatedFilters = [...updatedFilters, newFilterValue];
    }

    setController({ ...controller, filters: updatedFilters });
  };

  useEffect(() => {
    const { filters, range, page, lastDays, dateFilterType, ...rest } = controller;
    const params: Record<string, string> = {
      ...Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, String(v)])),
      filters: JSON.stringify(filters),
      page: page.toString(),
    };
    if (lastDays !== undefined) {
      params.lastDays = String(lastDays);
    } else {
      params.from = range[0].toISOString();
      params.to = range[1].toISOString();
    }
    setSearchParams(params);

    return () => {
      const allFilters = JSON.parse(storage.controllers.get() || '{}');
      storage.controllers.set(JSON.stringify({ ...allFilters, jobs: controller }));
    };
  }, [controller]);

  useEffect(() => {
    setController(setDefaultControllerFromSearchParams(searchParams));
  }, [tenant]);

  return (
    <>
      <Helmet>
        <title>Activity: List | {getHelmetTitle()}</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          flexGrow: 1,
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Box sx={{ py: 4 }}>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
              }}
            >
              <Typography color="textPrimary" variant="h4">
                Events
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
            </Box>
          </Box>
          <Card
            variant="outlined"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
            }}
          >
            <JobsFilter
              disabled={isLoading}
              onRangeChange={handleRangeChange}
              onQueryChange={handleQueryChange}
              onViewChange={handleViewChange}
              onFilterChange={handleFilterChange}
              range={controller.range}
              query={controller.query}
              view={controller.view}
              filters={controller.filters}
              lastDays={controller.lastDays}
              dateFilterType={controller.dateFilterType}
            />
            <Divider />
            <JobsTable
              error={error?.toString()}
              isLoading={isLoading}
              onPageChange={handlePageChange}
              onSortChange={handleSortChange}
              page={controller.page + 1}
              jobs={data?.data}
              jobsCount={data?.recordsFiltered}
              sort={controller.sort}
              sortBy={controller.sortBy}
            />
          </Card>
        </Container>
      </Box>
    </>
  );
};
